// Transforms do DESSEM são named exports por convenção (consumidos por
// relatorios.tsx); mais transforms entram nos planos seguintes.
import {
  addDays, format, isValid, parse, parseISO,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { GenericResponse } from 'services/api/base';
import {
  DessemOptions, DessemPremissas, DessemCarga, DessemCargaLiquida, DessemGeracaoRenovavel,
  DessemVisaoGeral, DessemResultados, DessemPremissasEdicao,
} from 'types/Relatorios/Dessem';

const FORMATO_DATA_RODADA = 'yyyy-MM-dd';

/**
 * `'YYYY-MM-DD'` (campos `*_data_rodada` do options) -> `Date` à meia-noite LOCAL, ou `null`
 * se ausente/inválida (ex.: `'2026-13-45'`, data-hora, outro formato). `parse` com o formato
 * fixo é estrito (rejeita sobras e mês/dia fora da faixa); meia-noite local garante que
 * `getDate()` não muda de dia com o fuso. Quem chama decide o fallback.
 */
export const parseDataRodadaIso = (iso: unknown): Date | null => {
  if (typeof iso !== 'string') return null;
  const data = parse(iso, FORMATO_DATA_RODADA, new Date());
  return isValid(data) ? data : null;
};

/**
 * `'2026-05-21'` -> `'quinta-feira, 21 de maio de 2026'` (cabeçalho da Visão Geral e estado
 * "sem informe"). Sempre via `parseISO` (nunca `new Date(string)`); uma data inválida volta
 * como veio, em vez de estourar o `format`.
 */
export const formatarDataPorExtenso = (dataRodadaIso: string): string => {
  const data = parseISO(dataRodadaIso);
  if (!isValid(data)) return dataRodadaIso;
  return format(data, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR });
};

export interface DatasDoInforme {
  /** Dia do deck oficial CCEE que serve de base à montagem (D). */
  d: Date;
  /** Dia previsto pelo deck DESSEM montado pela Ampere (D+1). */
  dMais1: Date;
}

/**
 * D e D+1 do informe, como nas "Considerações" do relatório do Planejamento: "deck DESSEM para o
 * dia D+1 com base no deck oficial CCEE do dia D". Desde 13/10/2026 `data_rodada` é a Data do
 * Informe (D, o dia escolhido no calendário); o dia previsto (D+1, `dt_ref` do estudo) vem depois.
 * `null` para data inválida (quem chama cai no texto antigo).
 */
export const datasDoInforme = (dataRodadaIso: string): DatasDoInforme | null => {
  const d = parseISO(dataRodadaIso);
  if (!isValid(d)) return null;
  return { d, dMais1: addDays(d, 1) };
};

/**
 * `'2026-10-13'` -> `'13/10/2026 (previsão para 14/10)'`: a data do informe com o dia previsto,
 * usada no aviso de topo, no painel de aprovação e no estado vazio. Data inválida volta como veio.
 */
export const rotuloDataDoInforme = (dataRodadaIso: string): string => {
  const datas = datasDoInforme(dataRodadaIso);
  if (datas === null) return dataRodadaIso;
  return `${format(datas.d, 'dd/MM/yyyy')} (previsão para ${format(datas.dMais1, 'dd/MM')})`;
};

// Tabela própria: o `EEEEEE` do locale ptBR do date-fns escreve "sab", sem acento.
const DIA_DA_SEMANA_CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** `Date` -> `'ter 13/10/2026'` (dia da semana curto, sem ponto, em pt-BR). */
export const formatarDataCurtaComSemana = (data: Date): string => (
  `${DIA_DA_SEMANA_CURTO[data.getDay()]} ${format(data, 'dd/MM/yyyy')}`
);

/**
 * Frase das "Considerações" do relatório do Planejamento, resumida: lida pelo leitor de tela e
 * mostrada no `title` da faixa de contexto (a linha do tempo visual é `aria-hidden`).
 */
export const fraseDoInforme = ({ d, dMais1 }: DatasDoInforme): string => (
  'Deck DESSEM montado para D+1 com base no deck oficial CCEE de D, sendo '
  + `D = ${format(d, 'dd/MM/yyyy')} e D+1 = ${format(dMais1, 'dd/MM/yyyy')}.`
);

export const transformDessemOptionsResponse = (
  response: GenericResponse<DessemOptions>,
): DessemOptions => response.data;

export const transformDessemPremissasResponse = (
  response: GenericResponse<DessemPremissas>,
): DessemPremissas => response.data;

/** `data` de `GET aprovacao/premissas` (estado do editor das Premissas Gerais, só admin). */
export const transformDessemPremissasEdicaoResponse = (
  response: GenericResponse<DessemPremissasEdicao>,
): DessemPremissasEdicao => response.data;

export const transformDessemCargaResponse = (
  response: GenericResponse<DessemCarga>,
): DessemCarga => response.data;

export const transformDessemCargaLiquidaResponse = (
  response: GenericResponse<DessemCargaLiquida>,
): DessemCargaLiquida => response.data;

export const transformDessemGeracaoRenovavelResponse = (
  response: GenericResponse<DessemGeracaoRenovavel>,
): DessemGeracaoRenovavel => response.data;

export const transformDessemVisaoGeralResponse = (
  response: GenericResponse<DessemVisaoGeral>,
): DessemVisaoGeral => response.data;

export const transformDessemResultadosResponse = (
  response: GenericResponse<DessemResultados>,
): DessemResultados => response.data;
